/**
 * Flip, as one implementation.
 *
 * Flip is an ACTION, never a stored property. How the mirror is recorded depends on whether the type owns its geometry:
 *
 *   - `brush` — mirrored destructively in `data`; it carries no scale, and every resize bakes.
 *   - SCALE-REALIZED (`path`/`rect`/`circle`/`image`/`group`) — the SIGN of `scaleX`/`scaleY`.
 *
 * There is no `flipX`/`flipY` boolean. Two of them existed until the scale cutover, giving image/video a third mechanism whose offset math had to be undone by hand wherever a group scale was inherited — the double-correction hazard `objectBounds` warns about.
 *
 * This module exists because the routine was written out twice (the AlignFlip panel and the keyboard thunk) and the copies had already drifted: same algorithm, one with its helpers extracted and one with them inlined. Both dispatch the patches themselves; this returns them.
 */
import { getObjectBounds, localExtent, localPivot, localToParent } from '~/lib/objectBounds'
import { flipPathGeometry } from '~/lib/svg/paths'

/** Types whose geometry we mirror directly rather than recording a sign. */
export const VECTOR_TYPES = new Set(['path', 'rect', 'circle', 'brush'])

/** Local flip matrix: negate one axis in local (path) space. */
export const getFlipMatrix = axis => (axis === 'x' ? { a: -1, b: 0, c: 0, d: 1 } : { a: 1, b: 0, c: 0, d: -1 })

/** Mirror a vector object's geometry in place, about its box's centre. Returns a patch, or null if it has no path data.
 *
 * The data is rewritten, and the new extent can differ from the old (bezier extrema), so the pivot — a fraction of the extent — moves: at a centred pivot the position IS the box centre and stays; otherwise it is re-placed against the preserved centre. */
export function flipVectorObject(obj, axis, objectsMap) {
	if (!obj.data) return null
	const { a, d } = getFlipMatrix(axis)
	const e = localExtent(obj, objectsMap)
	const centre = localToParent(obj, objectsMap, { x: e.x + e.width / 2, y: e.y + e.height / 2 })

	const flipped = flipPathGeometry(obj, a, d)
	const next = { ...obj, data: flipped.data }
	const ne = localExtent(next)
	const np = localPivot(next, null, ne)
	const rotRad = ((obj.rotation ?? 0) * Math.PI) / 180
	const cos = Math.cos(rotRad),
		sin = Math.sin(rotRad)
	const dx = np.x - (ne.x + ne.width / 2),
		dy = np.y - (ne.y + ne.height / 2)
	const pos = { x: centre.x + dx * cos - dy * sin, y: centre.y + dx * sin + dy * cos }
	const newBounds = getObjectBounds({ ...next, x: 0, y: 0 }, {})

	const result = {
		id: obj.id,
		data: flipped.data,
		x: Math.round(pos.x * 100) / 100,
		y: Math.round(pos.y * 100) / 100,
		width: newBounds.width,
		height: newBounds.height,
	}

	if (flipped.centerline) result.centerline = flipped.centerline
	if (flipped.rawPoints != null) {
		// Re-anchor rawPoints to the new data origin
		const origin = localToParent({ ...next, ...result }, null, { x: 0, y: 0 })
		const rp = flipped.rawPoints.split(',').map(Number)
		for (let k = 0; k < rp.length; k += 2) {
			rp[k] += origin.x
			rp[k + 1] += origin.y
		}
		result.rawPoints = rp.join(',')
	}

	return result
}

/** A group's children's extent in its own unscaled frame — their rotated footprints, as the renderer and the bounds read it. */
export function groupChildBounds(obj, objectsMap) {
	const e = localExtent(obj, objectsMap)
	if (!e.width && !e.height) return { cMinX: Infinity, cMinY: Infinity, cMaxX: -Infinity, cMaxY: -Infinity }
	return { cMinX: e.x, cMinY: e.y, cMaxX: e.x + e.width, cMaxY: e.y + e.height }
}

/**
 * Negate a group's scale and shift it so the children stay put.
 *
 * A group scales about its own origin — its pivot, which drifts off the box centre as its children are edited — so negating alone would move the box. The shift is `(min + max) * scale` along the flipped axis, carried through the group's rotation; zero while the children are still centred on the origin.
 */
export function flipGroup(obj, axis, objectsMap) {
	const sx = obj.scaleX ?? 1,
		sy = obj.scaleY ?? 1
	const update = { id: obj.id }
	if (axis === 'x') update.scaleX = -sx
	else update.scaleY = -sy

	const { cMinX, cMinY, cMaxX, cMaxY } = groupChildBounds(obj, objectsMap)

	const rotRad = ((obj.rotation ?? 0) * Math.PI) / 180
	const cos = Math.cos(rotRad),
		sin = Math.sin(rotRad)
	if (axis === 'x') {
		const dx = (cMinX + cMaxX) * sx
		update.x = Math.round((obj.x + dx * cos) * 100) / 100
		update.y = Math.round((obj.y + dx * sin) * 100) / 100
	} else {
		const dy = (cMinY + cMaxY) * sy
		update.x = Math.round((obj.x - dy * sin) * 100) / 100
		update.y = Math.round((obj.y + dy * cos) * 100) / 100
	}
	return update
}

/**
 * The patch that flips ONE object about `axis` ('x' | 'y'), or null if it cannot be flipped.
 *
 * Dispatching is the caller's job — the panel wraps each patch with its alt-key "global flip" extras, the thunk does not.
 */
export function buildFlipUpdate(obj, axis, objectsMap) {
	if (obj.type === 'brush' && obj.data) return flipVectorObject(obj, axis, objectsMap)
	if (obj.type === 'group') return flipGroup(obj, axis, objectsMap)

	// image and path-like vectors: the mirror IS the sign of the scale, turned about the pivot.
	const update = { id: obj.id }
	if (axis === 'x') update.scaleX = -(obj.scaleX ?? 1)
	else update.scaleY = -(obj.scaleY ?? 1)
	return update
}
